/**
 * Branded string types for compile-time key safety.
 *
 * JavaScript objects can only use strings/symbols as keys, so we can't use
 * UuidT objects directly. Instead, these branded types ensure that a BankKey
 * cannot be passed where a LiftKey is expected, etc.
 *
 * At runtime these are plain strings — zero cost.
 */

import { UuidT } from "../flatbuffers/generated/front-end/uuid";
import { FaultContent } from "../flatbuffers/generated/fault/fault-content";
import { uuidToString } from "./uuid";

// ---------------------------------------------------------------------------
// Branded string types — compile-time only
// ---------------------------------------------------------------------------
declare const BankKeyBrand: unique symbol;
declare const LiftKeyBrand: unique symbol;
declare const CompoundKeyBrand: unique symbol;
declare const BitmaskKeyBrand: unique symbol;
declare const FaultKeyBrand: unique symbol;

/** Key for byBank lookups — the bank UUID string */
export type BankKey = string & { readonly [BankKeyBrand]: void };

/** Key for byLift lookups — the lift UUID string */
export type LiftKey = string & { readonly [LiftKeyBrand]: void };

/** Compound key "bankId:liftId" for position/service maps */
export type CompoundKey = string & { readonly [CompoundKeyBrand]: void };

/** Compound key "bankId:liftId:Type:Value" for bitmask maps */
export type BitmaskKey = string & { readonly [BitmaskKeyBrand]: void };

/** Compound key "bankId:liftId:faultContent:code" for fault tracking */
export type FaultKey = string & { readonly [FaultKeyBrand]: void };

// ---------------------------------------------------------------------------
// Factory functions — accept UuidT, return branded keys
// ---------------------------------------------------------------------------

/** Build a BankKey from a bank UUID */
export function bankKey(bankId: UuidT): BankKey {
  return uuidToString(bankId) as BankKey;
}

/** Build a LiftKey from a lift UUID */
export function liftKey(liftId: UuidT): LiftKey {
  return uuidToString(liftId) as LiftKey;
}

/** Build a CompoundKey "bankId:liftId" for position/service maps */
export function compoundKey(bankId: UuidT, liftId: UuidT): CompoundKey {
  return `${uuidToString(bankId)}:${uuidToString(liftId) || ""}` as CompoundKey;
}

/** Build a BitmaskKey "bankId:liftId:Type:Value" */
export function bitmaskKey(
  bankId: UuidT,
  liftId: UuidT,
  type: string,
  value: string
): BitmaskKey {
  return `${uuidToString(bankId)}:${
    uuidToString(liftId) || ""
  }:${type}:${value}` as BitmaskKey;
}

/** Build a FaultKey "bankId:liftId:faultContent:code" */
export function faultKey(
  bankId: string,
  liftId: string,
  faultContent: number,
  code: number
): FaultKey {
  return `${bankId}:${liftId}:${faultContent}:${code}` as FaultKey;
}

/** Build the bitmask prefix "bankId:liftId:" for prefix-matching lookups */
export function bitmaskPrefix(bankId: UuidT, liftId: UuidT): string {
  return `${uuidToString(bankId)}:${uuidToString(liftId) || ""}:`;
}

/** Build the fault key prefix "bankId:liftId:" for prefix-matching lookups */
export function faultKeyPrefix(bankId: string, liftId: string): string {
  return `${bankId}:${liftId}:`;
}

/**
 * Numeric dedup key for fault identity within a single lift.
 * FaultContent has 3 variants (0-2), codes go up to 208,
 * so `faultType * 1000 + code` is collision-free and avoids string allocation.
 */
export function faultNumericKey(faultType: FaultContent, code: number): number {
  return faultType * 1000 + code;
}
